import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import { useFilters } from '@domain-group/react-hooks'

import { Box, Button, DashboardModule } from '@domain-group/suit-theme'

import { confirmDelete } from '@functions/entity'

import type { Options as PublicationPaginationRulesOptions } from '@hooks/usePublicationPaginationRules'
import usePublicationPaginationRule from '@hooks/usePublicationPaginationRule'
import usePublicationPaginationRules from '@hooks/usePublicationPaginationRules'
import useSelectEntities from '@hooks/useSelectEntities'

import DashboardModuleFilters from '@components/DashboardModuleFilters'

import PaginationRulePublicationListItem from './PaginationRulePublicationListItem'

const buildPageFilterFields = () => {
  const style = {
    marginBottom: 16,
  }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title',
    },
  ]
}

const PaginationRulePublications = (props: { paginationRuleId: number }) => {
  const { paginationRuleId } = props

  const filterPayload = useFilters()
  const {
    pageFilters,
  } = filterPayload

  const options: PublicationPaginationRulesOptions = {
    filters: { pagination_rule_id: paginationRuleId, ...pageFilters },
    performHttpRequests: !!paginationRuleId,
    requestOptions: { include_publication_pagination_rule_publication: true },
  }

  const publicationPaginationRulesPayload = usePublicationPaginationRules(options)
  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPublicationPaginationRules,
    filteredPublicationPaginationRulesCount,
    hasFilteredPublicationPaginationRules,
    loading,
  } = publicationPaginationRulesPayload

  const {
    callbacks: { manageForPaginationRule, managePublicationPaginationRules },
  } = usePublicationPaginationRule({ pagination_rule_id: paginationRuleId })

  const unselectEntitiesPayload = useSelectEntities()
  const {
    callbacks: {
      getSelectableListItemProps,
      deselectAll,
    },
    hasMinimumSelected,
    selectedEntitiesCount,
    selectedEntityIds,
  } = unselectEntitiesPayload

  const confirmDeleteParams = {
    callbacks: {
      afterAction: deselectAll,
      buildActionParams: () => (
        [{ action: 'unassign', publication_ids: selectedEntityIds, pagination_rule_id: paginationRuleId }]
      ),
      deleteFn: manageForPaginationRule,
    },
    options: { isBulkDelete: true },
    strings: {
      deleteString: 'unassign',
      entityName: 'Publications',
    },
  }

  return (
    <DashboardModule
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={<DashboardModule.FilterHeader {...filterPayload} />}
      loading={loading}
      title="Publications"
    >
      <DashboardModuleFilters
        filterFields={buildPageFilterFields()}
        {...filterPayload}
      />

      <Box flexDirection="column">
        {!loading && !hasFilteredPublicationPaginationRules && (
          <DashboardModule.BlankState>
            <DashboardModule.BlankState.Title>
              No Publications present
            </DashboardModule.BlankState.Title>

            <DashboardModule.BlankState.Paragraph>
              Please assign a Publication below.
            </DashboardModule.BlankState.Paragraph>
          </DashboardModule.BlankState>
        )}

        {!loading && hasFilteredPublicationPaginationRules && (
          // @ts-expect-error TS2322
          <DashboardModule.ScrollableSection flexDirection="column">
            {filteredPublicationPaginationRules.map((publicationPaginationRule) => {
              const listItemProps = getSelectableListItemProps(publicationPaginationRule.publication_id)

              return (
                <PaginationRulePublicationListItem
                  key={publicationPaginationRule.id}
                  publicationPaginationRule={publicationPaginationRule}
                  {...listItemProps}
                />
              )
            })}
          </DashboardModule.ScrollableSection>
        )}

        <DashboardModule.LoadMoreFooter
          boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredPublicationPaginationRulesCount}
          loading={loading}
        >
          {hasMinimumSelected && (
            <Button
              buttonStyle="secondaryEdit"
              icon={<FontAwesomeIcon icon={faTrashAlt} />}
              marginRight={[0, 'medium']}
              marginTop={['medium', 0]}
              // @ts-expect-error TS2345
              onClick={() => confirmDelete(confirmDeleteParams)}
              // @ts-expect-error TS2322
              size={['medium', 'small']}
              width={['100%', 'fit-content']}
            >
              Unassign Selected Publications ({selectedEntitiesCount})
            </Button>
          )}

          <Button
            buttonStyle="secondaryEdit"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => managePublicationPaginationRules({ action: 'assign' })}
            // @ts-expect-error TS2322
            size={['medium', 'small']}
            width={['100%', 'fit-content']}
          >
            Add Publications
          </Button>
        </DashboardModule.LoadMoreFooter>
      </Box>
    </DashboardModule>
  )
}

export default PaginationRulePublications
